import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  hierarchyLabelsService,
  type HierarchyLabels,
  type CustomFieldDef,
} from '../services/hierarchy-labels.service';
import { HIERARCHY_LABELS_QUERY_KEY } from '@/lib/current-hierarchy-text';
import { translateHierarchyTerms } from '@/lib/hierarchy-terms';

const LABELS_KEY = HIERARCHY_LABELS_QUERY_KEY;

export function useHierarchyLabels() {
  return useQuery({
    queryKey: LABELS_KEY,
    queryFn: () => hierarchyLabelsService.getLabels(),
    // Labels rarely change but are read on most pages — keep them fresh for a
    // few minutes to avoid hammering the backend on every navigation.
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateHierarchyLabels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (labels: Partial<HierarchyLabels>) => hierarchyLabelsService.updateLabels(labels),
    onSuccess: () => qc.invalidateQueries({ queryKey: LABELS_KEY }),
  });
}

export type HierarchyNode = 'site' | 'level' | 'zone' | 'tunnel' | 'team';

export interface ResolvedLabel {
  singular: string;
  plural: string;
  enabled: boolean;
  // HR-02 — tenant-defined custom field definitions for this node type.
  fields: CustomFieldDef[];
}

const DEFAULTS: Record<HierarchyNode, ResolvedLabel> = {
  site: { singular: 'Site', plural: 'Sites', enabled: true, fields: [] },
  level: { singular: 'Level', plural: 'Levels', enabled: true, fields: [] },
  zone: { singular: 'Zone', plural: 'Zones', enabled: true, fields: [] },
  tunnel: { singular: 'Tunnel', plural: 'Tunnels', enabled: true, fields: [] },
  team: { singular: 'Team', plural: 'Teams', enabled: true, fields: [] },
};

function autoPlural(singular: string): string {
  return `${singular}s`;
}

export function buildHierarchyLabelMap(
  data?: HierarchyLabels,
): Record<HierarchyNode, ResolvedLabel> {
  const disabled = new Set<string>(data?.disabledLevels ?? []);
  const cfMap = data?.customFields ?? {};
  const resolve = (
    node: HierarchyNode,
    singular: string | undefined,
    plural: string | null | undefined,
    fallback: ResolvedLabel,
  ): ResolvedLabel => {
    const trimmedSingular = singular?.trim();
    let s = fallback.singular;
    if (trimmedSingular) s = trimmedSingular;
    s = s.trim();
    const trimmedPlural = plural?.trim();
    let p = autoPlural(s);
    if (trimmedPlural) p = trimmedPlural;
    p = p.trim();
    // Site/Level/Zone are structural — never disableable. Only tunnel/team
    // honour the disabledLevels flag.
    const canBeDisabled = node === 'tunnel' || node === 'team';
    const enabled = canBeDisabled ? !disabled.has(node) : true;
    const fields = cfMap[node] ?? [];
    return { singular: s, plural: p, enabled, fields };
  };
  return {
    site: resolve('site', data?.siteLabel, data?.siteLabelPlural, DEFAULTS.site),
    level: resolve('level', data?.levelLabel, data?.levelLabelPlural, DEFAULTS.level),
    zone: resolve('zone', data?.zoneLabel, data?.zoneLabelPlural, DEFAULTS.zone),
    tunnel: resolve('tunnel', data?.tunnelLabel, data?.tunnelLabelPlural, DEFAULTS.tunnel),
    team: resolve('team', data?.teamLabel, data?.teamLabelPlural, DEFAULTS.team),
  };
}

// Returns a fully-resolved map of singular + plural labels (and enabled flag)
// for every hierarchy node. Plurals fall back to `${singular}s` when not
// explicitly set. Call sites: `const L = useHierarchyLabelMap(); L.site.singular`.
export function useHierarchyLabelMap(): Record<HierarchyNode, ResolvedLabel> {
  const { data } = useHierarchyLabels();
  return useMemo(() => buildHierarchyLabelMap(data), [data]);
}

export function useHierarchyText(): (text: string) => string {
  const labels = useHierarchyLabelMap();
  return useCallback((text: string) => translateHierarchyTerms(text, labels), [labels]);
}

// Thin convenience: just the set of disabled hierarchy levels.
export function useDisabledHierarchyLevels(): Set<HierarchyNode> {
  const { data } = useHierarchyLabels();
  return useMemo(
    () => new Set<HierarchyNode>(data?.disabledLevels ?? []),
    [data],
  );
}

export const HIERARCHY_LABEL_DEFAULTS = DEFAULTS;
